<template>
  <modal
    v-if="active"
    width="600px"
    bodyPadding="0"
    class="details-dialog"
    :title="title"
    @close="close"
    :read-article-url="readArticleUrl"
  >
    <div :class="contentClass">
      <div class="template-select-dialog__body">
        <div class="campaign-details template-select-dialog__body-inner">
          <div class="wrapper-ant template-select-dialog__body-inner">
            <div class="mod-search-abs">
              <ui-icon size="15" :color="$colors.gray40" icon="fas fa-search"></ui-icon>
              <v-text-field
                v-model="search"
                :placeholder="t('Search...')"
              ></v-text-field>
            </div>

            <div class="searchable-list">
              <div
                v-if="computedItems.length"
                class="list-cust"
                style="padding-left: 10px; padding-right: 10px"
              >
                <v-list class="template-select-dialog__list-item">
                  <v-list-item
                    v-for="item in computedItems"
                    :key="item.id"
                    @click="onUpdateActiveItem(item.id)"
                  >
                    <v-list-item-title>
                      <slot name="item" :item="item" :active-item="activeItem">
                        <span :class="{ activeItem: activeItem === item.id }">{{ item.name }}</span>
                      </slot>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
              <slot v-else name="no-items"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="justify-end xs12" style="display: flex">
        <ui-button
          :text="t('Cancel')"
          icon="fas fa-times"
          @click="close"
        ></ui-button>

        <ui-button
          :text="t('Next')"
          icon-append="fas fa-arrow-right"
          button-style="accent"
          :disabled="!activeItem"
          @click="onConfirm(activeItem)"
        ></ui-button>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useColors, useI18n } from '@/composables';

const props = withDefaults(
  defineProps<{
    items?: { id: string; name: string }[];
    readArticleUrl?: string;
    title?: string;
    hasActions?: boolean;
    hasHelp?: boolean;
  }>(),
  {
    items: () => [],
    hasActions: true,
    hasHelp: true,
  },
);

const emit = defineEmits<{
  'update:activeItem': [value: string | null];
  confirm: [value: string | null];
}>();

const { t } = useI18n();
const $colors = useColors();

const active = ref(false);
const search = ref('');
const activeItem = ref<string | null>(null);

const contentClass = computed(() => {
  const result = ['template-select-dialog'];
  if (!props.hasActions) {
    result.push('template-select-dialog--hide-actions');
  }
  return result.join(' ');
});

const computedItems = computed(() => {
  const list = props.items ?? [];
  return list.filter((item) =>
    String(item.name ?? '')
      .toLowerCase()
      .includes(search.value.toLowerCase()),
  );
});

const onUpdateActiveItem = (value: string) => {
  activeItem.value = value;
  emit('update:activeItem', value);
};

const onConfirm = (value: string | null) => {
  emit('confirm', value);
};

const open = () => {
  activeItem.value = null;
  search.value = '';
  active.value = true;
};

const close = () => {
  active.value = false;
};

defineExpose({ open, close });
</script>

<style lang="scss">
  .template-select-dialog {
    height: 100%;
    max-height: 700px !important;

    .v-card {
      height: 100%;
      max-height: 700px;
      display: flex;
      flex-direction: column;
    }

    &--hide-actions {
      &.v-dialog.v-dialog--active > .v-card > .v-card__text {
        flex-grow: 1;
      }

      .v-card__actions {
        display: none;
      }
    }

     &__list-item {
      & + & {
        border-top: 1px solid var(--color-gray10);
      }
     }

    &.v-dialog.v-dialog--active .create-campaign__body {
      .campaign-details {
        overflow-y: unset;
      }
    }

    &__body {
      overflow-y: hidden;
    }

    &__body-inner {
      height: 100%;
    }
  }

  .mod-search-abs {
      position: relative;
      display: flex;
      min-height: 43px;

      .ui-icon {
        z-index: 3;
        position: absolute;

        top: calc(50% - 7px);
        left: 16px;
      }

      .v-input {
        padding: 0 !important;
      }

      .v-input__slot {
        margin-bottom: 0 !important;
      }

      .v-input__control {
        height: 43px;
      }

      input {
        padding-left: 45px !important;
        margin: 0 !important;
        border: none !important;
        border-radius: 0 !important;
        min-height: 43px;
        height: 43px;
        border-bottom: 1px solid var(--color-gray10) !important;

        &::placeholder {
          opacity: 0.4;
          font-size: 15px;
        }
      }
  }

  .wrapper-ant {
    display: flex;
    flex-direction: column;

    .searchable-list {
      overflow-y: auto;
    }

    .list-cust {
      display: flex;
      justify-content: flex-start;
      flex-direction: column;

      .v-list {
        padding: 0;

        span {
          padding: 2px 20px;
        }

        .activeItem {
          background: var(--color-yellowlight);
        }
      }

      span {
        display: block;
        font-size: 20px;
        width: 100%;

        &:hover {
          cursor: pointer;
          background: var(--color-yellowlight);
        }
      }
    }
  }
</style>
